import { useCallback, useMemo, useRef, useState } from "react";
import { useBoolean } from "@chakra-ui/react";
import debounce from "lodash.debounce";
import isEqual from "lodash.isequal";
import { IExtendedSearchItem, useExtendedSearchMutation } from "src/api/client/extended-search.api";
import { ISearchRequestParamsOmitCountry } from "src/components/pages/search/search.types";
import { logger } from "src/utils/logger-util";
import { useRouterLocale } from "src/utils/router/use-router-locale";

const WAIT_MILLISECONDS = 650;
const SEARCH_WIKIFOLIOS_ERROR_MESSAGE = "An error has occured while searching for wikifolios.";

export const useSearch = () => {
    const [resultCount, setResultCount] = useState<number | null>(null);
    const [results, setResults] = useState<IExtendedSearchItem[] | null>(null);
    const [isLoading, setIsLoading] = useBoolean(false);
    const [isFailed, setIsFailed] = useBoolean(false);

    const { country } = useRouterLocale();
    const previousParams = useRef<ISearchRequestParamsOmitCountry | null>(null);
    const searchMutation = useExtendedSearchMutation();

    const onSearch = useCallback(
        async (params: ISearchRequestParamsOmitCountry) => {
            const shouldSearchBeOmitted = isEqual(params, previousParams.current);
            previousParams.current = params;

            if (shouldSearchBeOmitted) {
                return;
            }

            try {
                setIsLoading.on();

                const response = await searchMutation({
                    country,
                    ...params,
                });

                setResultCount(response.totalResults);

                //TODO: https://wikifolio.atlassian.net/browse/WIKI-31198
                const responseResults = response.results ?? [];

                if (params?.offset) {
                    setResults(prevResults => [...(prevResults ?? []), ...responseResults]);
                } else {
                    setResults(responseResults);
                }

                setIsFailed.off();
            } catch (error) {
                logger.error({ message: SEARCH_WIKIFOLIOS_ERROR_MESSAGE, error });
                setIsFailed.on();
            } finally {
                setIsLoading.off();
            }
        },
        [country, searchMutation, setIsFailed, setIsLoading]
    );

    const debounceApiCall = useMemo(() => debounce(onSearch, WAIT_MILLISECONDS, { leading: false, maxWait: 350 }), [onSearch]);

    return {
        onSearchDebounce: debounceApiCall,
        onSearch,
        resultCount,
        results,
        isLoading,
        isFailed,
        offset: previousParams.current?.offset,
    };
};
